import React, { useRef, useState } from 'react';
import { X, Upload, FileText } from 'lucide-react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../../firebase';
import { Folder } from '../../types/documents';
import toast from 'react-hot-toast';

interface UploadModalProps {
  onClose: () => void;
  onUpload: (files: FileList) => void;
  currentFolder?: Folder;
}

export default function UploadModal({ onClose, onUpload, currentFolder }: UploadModalProps) {
  const [files, setFiles] = useState<FileList | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!files?.length) return;

    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;

      // Upload each file
      for (const file of Array.from(files)) {
        try {
          // Create a reference to the file in Firebase Storage
          const storageRef = ref(storage, `documents/${user.uid}/${file.name}`);
          
          // Upload the file
          await uploadBytes(storageRef, file);
          
          // Get the download URL
          const url = await getDownloadURL(storageRef);

          // Create document record in Firestore
          await addDoc(collection(db, 'documents'), {
            name: file.name,
            type: file.type,
            size: file.size,
            url,
            folderId: currentFolder?.id || null,
            createdBy: user.uid,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            accessLevel: 'view',
            tags: [],
          });

          toast.success(`Uploaded ${file.name}`);
        } catch (error) {
          console.error('Error uploading file:', file.name, error);
          
          // Provide specific error messages for different types of failures
          if (error instanceof Error) {
            if (error.message.includes('CORS') || error.toString().includes('net::ERR_FAILED')) {
              toast.error(`Upload failed due to CORS policy. Please ensure Firebase Storage is properly configured for your domain.`);
            } else if (error.message.includes('storage/unauthorized')) {
              toast.error(`Upload failed: Insufficient permissions. Please check your account access.`);
            } else if (error.message.includes('storage/quota-exceeded')) {
              toast.error(`Upload failed: Storage quota exceeded. Please contact your administrator.`);
            } else if (error.message.includes('storage/invalid-format')) {
              toast.error(`Upload failed: ${file.name} is not a supported file type.`);
            } else if (error.message.includes('storage/object-not-found')) {
              toast.error(`Upload failed: Storage bucket not found. Please contact support.`);
            } else {
              toast.error(`Failed to upload ${file.name}: ${error.message}`);
            }
          } else {
            toast.error(`Failed to upload ${file.name}`);
          }
        }
      }

      onClose();
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      setFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-light rounded-xl shadow-lg w-full max-w-md max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border-light">
          <h2 className="text-xl font-semibold text-text-primary">Upload Documents</h2>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-8rem)]">
          <div className="mb-6">
            {currentFolder && (
              <p className="text-sm text-text-secondary mb-2">
                Uploading to folder: <span className="font-medium">{currentFolder.name}</span>
              </p>
            )}

            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-border-light rounded-lg p-8 text-center 
                       hover:border-primary-500 transition-colors cursor-pointer"
            >
              <Upload className="w-8 h-8 text-text-secondary mx-auto mb-3" />
              <p className="text-text-primary font-medium mb-1">
                Click to upload or drag and drop
              </p>
              <p className="text-sm text-text-secondary">
                PDF, Word, Excel, PowerPoint, and image files
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={(e) => setFiles(e.target.files)}
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif"
            />
          </div>

          {files && files.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-text-secondary mb-2">
                Selected Files ({files.length})
              </h3>
              <div className="space-y-2">
                {Array.from(files).map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 text-sm text-text-primary bg-background 
                             rounded-lg p-2"
                  >
                    <FileText className="w-4 h-4 text-primary-500" />
                    <span className="truncate flex-1">{file.name}</span>
                    <span className="text-text-secondary text-xs">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !files?.length}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                       transition-colors disabled:opacity-50 disabled:cursor-not-allowed 
                       flex items-center gap-2"
            >
              <Upload className="w-5 h-5" />
              {loading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}